
import Component, { mixins } from 'vue-class-component';
import WidgetMixins from '../../../mixins/WidgetMixins';
import DialogMixins from '../../../mixins/DialogMixins';
import { Getter, namespace } from 'vuex-class';
import {
  IFileDownloads,
  ILogs,
  ILogsGraph,
  IPaginate,
  ISenderID,
  IUser,
} from '@/types/types';
import { dateFormat, truncateContent, loadWidget } from '@/utils/helpers';
import { KX_STATUS_COLORS } from '@/utils/statusColors';
import { Watch } from 'vue-property-decorator';
import moment from 'moment';
import { debounce } from 'lodash';
import { from, throwError, timer } from 'rxjs';
import { catchError, switchMap, concatMap, tap } from 'rxjs/internal/operators';
import { map, takeWhile } from 'rxjs/operators';

const logsModule = namespace('log');
const smsModule = namespace('sms');
const usersModule = namespace('users');

@Component({
  name: 'Index',
  components: {
    InternetConnection: loadWidget('pages/InternetConnection'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
    EmptyPage: loadWidget('pages/EmptyPage'),
    DeleteDialog: loadWidget('widgets/DeleteDialog'),
    ProgressBar: loadWidget('widgets/CircularProgressLoader'),
    DoubleDatePicker: loadWidget('widgets/DoubleDatePicker'),
  },
  filters: {
    dateFormat,
    truncateContent,
  },
})
export default class Index extends mixins(WidgetMixins, DialogMixins) {
  @Getter('getCreditBalance') creditBalance!: number;
  @logsModule.Getter('getPagination') paginate!: IPaginate & { limit: number };
  @logsModule.Getter('getAllLogs') logs!: ILogs[];
  @logsModule.Getter('getLogsGraph') logsGraph!: Array<ILogsGraph>;
  @smsModule.Getter('getSenderIDList') approvedSenderIDlist!: Array<
    ISenderID & { id: number; slug: string; uuid: string }
  >;
  @usersModule.Getter('getCustomerDetails') customerDetails!: IUser;
  @usersModule.Getter('getSearchResults') subAccounts!: IUser[];
  menu = false;
  renderChart = false;
  options: IPaginate = {
    itemsPerPage: 15,
    page: 1,
    total: 0,
  };
  subAccountId: Pick<IUser, 'id'> | undefined | null = null;
  breadcrumbs = [{ text: 'Downloads', href: '/reports/downloads' }];
  headers: Array<{ text: string; value: string; align?: string }> = [
    {
      text: 'Date',
      value: 'updatedAt',
    },
    {
      text: 'Status',
      value: 'status',
    },

    {
      text: 'Type',
      value: 'type',
    },
    {
      text: 'Name',
      value: 'name',
    },
    {
      text: 'File Size',
      value: 'fileSize',
    },
    { text: 'Action', value: 'action', align: 'center' },
  ];
  searchTerm = '';
  senderIdFilters: ISenderID[] = [];
  selectedFileId: string | null = null;
  fileExtension = '';
  statusColors: { [key: string]: { bg: string; text: string } } = {
    'IN-PROGRESS': KX_STATUS_COLORS.pending,
    FAILED: KX_STATUS_COLORS.failed,
    COMPLETED: KX_STATUS_COLORS.success,
  };
  series: Array<{ name: string; data: number[] }> = [];

  dateFilter = '';
  page = 1;
  dataLoaderPlaceholder = 'Loading data ...';

  handleOnSearchTextChange = debounce(this.searchText, 2000);

  searchText(text: string): void {
    this.$store.dispatch('log/listAllFileDownloads', {
      page: this.page,
      limit: this.options?.itemsPerPage,
      query: `${this.dateFilter}${text ? '&searchTerm=' + text : ''}`,
      subAccountId: this.$route.query?.id,
    });
  }

  @Watch('$route.query.id')
  onRouteIDChange(id: string | undefined): void {
    this.$store.dispatch('log/listAllFileDownloads', {
      page: this.page,
      limit: this.options?.itemsPerPage,
      query: `${this.dateFilter}${
        this.searchTerm ? '&searchTerm=' + this.searchTerm : ''
      }`,
      subAccountId: id,
    });
  }

  @Watch('searchTerm')
  onSearchInputTextBoxChange(searchTerm: string): void {
    this.handleOnSearchTextChange(searchTerm);
  }

  @Watch('subAccountId')
  onSubAccountIdChange(account: Pick<IUser, 'id'>): void {
    if (!account) {
      this.$router.push({
        query: undefined,
      });
      return;
    }
    this.$router.push({
      query: {
        id: account.id,
      },
    });
    this.subAccountId = account;
  }

  @Watch('page')
  onOptionsChange(page: number): void {
    this.$store.dispatch('log/listAllFileDownloads', {
      page: page ?? 1,
      limit: this.paginate.limit ?? 15,
      query: `${this.dateFilter}${
        this.searchTerm ? '&searchTerm=' + this.searchTerm : ''
      }`,
      subAccountId: this.$route.params.id,
    });
  }

  filterLogsByDate(date: string): void {
    this.page = 1;
    this.dateFilter = `&date=${date}`;
    this.$store.dispatch('log/listAllFileDownloads', {
      page: this.page,
      limit: this.paginate.limit,
      query: `${this.dateFilter}${
        this.searchTerm ? '&searchTerm=' + this.searchTerm : ''
      }`,
      subAccountId: this.$route.params.id ?? this.$route.query?.id,
    });
  }

  exportFile(file: IFileDownloads & { _id: string }): void {
    this.selectedFileId = file._id;
    this.$store.dispatch('log/fileDownload', {
      subAccountId: this.$route.params.id ?? this.$route.query?.id,
      id: file._id,
    });
  }

  confirmFileDelete(id: string | number): void {
    this.selectedFileId = id as string;
    this.open('delete');
  }

  deleteFile(): void {
    if (this.selectedFileId !== null) {
      this.$store.dispatch('log/deleteDownloadedFile', {
        id: this.selectedFileId,
        query: `${this.dateFilter}${
          this.searchTerm ? '&searchTerm=' + this.searchTerm : ''
        }`,
        subAccountId: this.$route.params.id ?? this.$route.query?.id,
      });
      return;
    }
    this.close({
      state: false,
      idx: 'delete',
    });
  }

  created(): void {
    this.page = 1;
    this.dateFilter = `&date=${moment()
      .subtract(1, 'month')
      .format('YYYY-MM-DD')},${moment().format('YYYY-MM-DD')}`;
    const downloadFilesQuery = {
      page: this.page,
      limit: 15,
      query: this.dateFilter,
      subAccountId: this.$route.params.id ?? this.$route.query?.id,
    };
    this.$store.dispatch('log/listAllFileDownloads', downloadFilesQuery);
    this.$store.dispatch('users/rawList');
    if (this.$route.params.id) {
      this.subAccountId = { id: this.$route.params.id };
      this.$store.dispatch('users/customerDetails', {
        accountId: this.$route.params.id ?? this.$route.query?.id,
      });
    }
    timer(3000, 15000)
      .pipe(
        concatMap(() => from(this.$store.getters['log/getAllLogs'])),
        takeWhile((val: any) => val.status === 'IN-PROGRESS', true),
        switchMap(() =>
          from(
            this.$store.dispatch('log/listAllFileDownloads', downloadFilesQuery)
          ).pipe(map((d) => d))
        ),
        catchError((error) => {
          console.error(error);
          return throwError(() => error);
        })
      )
      .subscribe();
  }
}
